import { farmDayKey, farmWallClockToInstant, isCalendarDayValue, isWallClockValue } from './farmTime'

// Findings #203: one stock rule for a count day that also has deaths. A count is
// the stock at the moment it was taken; the deaths of that day recorded after it
// are taken off, the ones before it (or at the same moment, e.g. one Hoja diaria
// line) are already out of the count. A count with no time (a calendar day, e.g.
// a stocking count) is the stock at the start of the day: all that day's deaths
// come off it. The overlays carry the times on the row:
//   fishStockCountedAtMs — the winning count's moment
//   mortalityTimeline    — "ms:count,ms:count" for that day's MortalityLogs

const instantMs = (raw: unknown, timeZone: string): number | null => {
  if (typeof raw !== 'string' || !raw.trim()) return null
  const trimmed = raw.trim()
  if (isCalendarDayValue(trimmed) || isWallClockValue(trimmed)) {
    const local = isCalendarDayValue(trimmed) ? `${farmDayKey(trimmed, timeZone)}T00:00` : trimmed.slice(0, 16)
    const instant = farmWallClockToInstant(local, timeZone)
    const ms = instant ? Date.parse(instant) : Number.NaN
    return Number.isFinite(ms) ? ms : null
  }
  const ms = Date.parse(trimmed)
  return Number.isFinite(ms) ? ms : null
}

/** The moment a count stands for; a calendar-day count sits before every record of its day. */
export const countInstantMs = (recordedAt: unknown, timeZone: string): number | null => {
  const ms = instantMs(recordedAt, timeZone)
  if (ms === null) return null
  return isCalendarDayValue(String(recordedAt).trim()) ? ms - 1 : ms
}

/** The moment a death record stands for; a calendar day is the start of that day. */
export const mortalityInstantMs = (recordedAt: unknown, timeZone: string): number | null => instantMs(recordedAt, timeZone)

export const encodeMortalityTimeline = (entries: ReadonlyArray<{ ms: number; count: number }>): string => (
  entries.map((entry) => `${entry.ms}:${entry.count}`).join(',')
)

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || !value.trim()) return null
  const parsed = Number.parseFloat(value.trim())
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Deaths of the count's day recorded after the count, or null when the row
 * carries no times (records from before the event logs): the caller then keeps
 * the old day rule.
 */
export const deathsAfterCount = (row: { fishStockCountedAtMs?: string; mortalityTimeline?: string; dailyMortality?: string }): number | null => {
  const countedAt = parseNumeric(row.fishStockCountedAtMs)
  if (countedAt === null) return null
  const timeline = typeof row.mortalityTimeline === 'string' ? row.mortalityTimeline.trim() : ''
  if (!timeline) {
    const dayMortality = parseNumeric(row.dailyMortality)
    return dayMortality !== null && dayMortality > 0 ? null : 0
  }
  let after = 0
  for (const entry of timeline.split(',')) {
    const [ms, count] = entry.split(':').map(Number)
    if (Number.isFinite(ms) && Number.isFinite(count) && count > 0 && ms > countedAt) after += count
  }
  return after
}
